<template>
  <div
    class="dash-card critical-tasks-panel"
    :class="{ 'is-alarming': totalPending > 0 }"
  >
    <div class="critical-header">
      <div class="critical-header-left">
        <span class="critical-header-icon">
          <a-icon type="alert" />
        </span>
        <div>
          <h3 class="critical-title">
            {{ $t("accueil.dashboard.criticalTasks") }}
          </h3>
          <p class="critical-subtitle">
            {{ $t("accueil.dashboard.criticalTasksSubtitle") }}
          </p>
        </div>
      </div>

      <div v-if="totalPending > 0" class="critical-badge">
        <strong>{{ totalPending }}</strong>
        <span>{{ $t("accueil.dashboard.enAttente") }}</span>
      </div>
      <div v-else class="critical-badge critical-badge--clear">
        <a-icon type="check-circle" />
        <span>{{ $t("accueil.dashboard.allClear") }}</span>
      </div>
    </div>

    <div class="critical-list">
      <button
        v-for="task in taskRows"
        :key="task.key"
        type="button"
        class="critical-item dash-focusable"
        :class="{ 'is-empty': task.count === 0 }"
        :style="{ '--accent': task.color }"
        @click="handleTaskClick(task)"
      >
        <span class="critical-item-bar" />
        <span
          class="critical-item-icon"
          :style="{ color: task.color, backgroundColor: task.color + '18' }"
        >
          <a-icon :type="task.icon" />
        </span>
        <span class="critical-item-label">{{ task.label }}</span>
        <span
          v-if="task.count > 0"
          class="critical-item-count"
          :style="{ backgroundColor: task.color }"
          >{{ task.count }}</span
        >
        <a-icon v-else type="check" class="critical-item-ok" />
        <a-icon type="right" class="critical-item-arrow" />
      </button>
    </div>

    <div v-if="showNewsCta" class="critical-news">
      <div class="critical-news-icon">
        <a-icon type="notification" />
      </div>
      <div class="critical-news-text">
        <strong>{{ newsCtaTitle }}</strong>
        <span>{{ newsCtaSubtitle }}</span>
      </div>
      <a-button type="primary" size="small" @click="goTo('/actuality')">
        <a-icon type="plus" />
        {{ $t("accueil.dashboard.addNews") }}
      </a-button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  canShowFeature,
  DASHBOARD_FEATURES,
} from "../utils/dashboardAccess";

export default {
  name: "CriticalTasksPanel",
  props: {
    tasks: {
      type: Object,
      default: () => ({
        punitionsPendingValidation: 0,
        notificationsUnread: 0,
        messagesParentsUnanswered: 0,
        actualitesPendingValidation: 0,
        actualites: {
          totalCount: 0,
          publishedToday: false,
        },
      }),
    },
  },
  computed: {
    ...mapState(["user", "settings"]),
    canShowActualite() {
      return canShowFeature(
        this.user,
        this.settings,
        DASHBOARD_FEATURES.actualite
      );
    },
    showNewsCta() {
      if (!this.canShowActualite) return false;
      return (
        !this.tasks.actualites ||
        this.tasks.actualites.totalCount === 0 ||
        !this.tasks.actualites.publishedToday
      );
    },
    newsCtaTitle() {
      if (!this.tasks.actualites || this.tasks.actualites.totalCount === 0) {
        return this.$t("accueil.dashboard.newsCtaFirstTitle");
      }
      return this.$t("accueil.dashboard.newsCtaTodayTitle");
    },
    newsCtaSubtitle() {
      if (!this.tasks.actualites || this.tasks.actualites.totalCount === 0) {
        return this.$t("accueil.dashboard.newsCtaFirstSubtitle");
      }
      return this.$t("accueil.dashboard.newsCtaTodaySubtitle");
    },
    taskRows() {
      const t = this.tasks || {};
      const u = this.user;
      const s = this.settings;
      const rows = [
        {
          key: "punitions",
          icon: "safety-certificate",
          color: "#fa8c16",
          label: this.$t("accueil.dashboard.punitionsPending"),
          count: t.punitionsPendingValidation || 0,
          route: "/listePunitions",
          feature: DASHBOARD_FEATURES.punition,
        },
        {
          key: "notifications",
          icon: "bell",
          color: "#f5222d",
          label: this.$t("accueil.dashboard.notificationsUnread"),
          count: t.notificationsUnread || 0,
          route: "/notifications",
          feature: DASHBOARD_FEATURES.notifications,
        },
        {
          key: "messages",
          icon: "message",
          color: "#13c2c2",
          label: this.$t("accueil.dashboard.messagesUnanswered"),
          count: t.messagesParentsUnanswered || 0,
          route: "/chat",
          feature: DASHBOARD_FEATURES.chat,
        },
        {
          key: "actualites",
          icon: "file-text",
          color: "#1890ff",
          label: this.$t("accueil.dashboard.actualitesPending"),
          count: t.actualitesPendingValidation || 0,
          route: "/actuality",
          feature: DASHBOARD_FEATURES.actualite,
        },
      ];
      return rows.filter((row) => canShowFeature(u, s, row.feature));
    },
    totalPending() {
      return this.taskRows.reduce((sum, task) => sum + (task.count || 0), 0);
    },
  },
  methods: {
    goTo(path) {
      if (!path) return;
      if (this.$route.path !== path) {
        this.$router.push({ path });
      }
    },
    handleTaskClick(task) {
      this.goTo(task.route);
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes critical-border-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 34, 45, 0.35);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 34, 45, 0);
  }
}

.critical-tasks-panel {
  padding-bottom: 8px;
  border: 2px solid #f5222d;

  &.is-alarming {
    animation: critical-border-pulse 2.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
}

.critical-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--dash-border, #f0f0f0);
}

.critical-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.critical-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1f0;
  color: #f5222d;
  font-size: 18px;
  flex-shrink: 0;
}

.critical-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--dash-title-color, #1a1a1a);
}

.critical-subtitle {
  margin: 2px 0 0;
  font-size: 0.8rem;
  color: var(--dash-muted, #8c8c8c);
}

.critical-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: #fff1f0;
  color: #cf1322;
  flex-shrink: 0;

  strong {
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1;
  }

  span {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &--clear {
    background: #f6ffed;
    color: var(--dash-success);
    font-weight: 600;
    font-size: 0.85rem;
  }
}

.critical-list {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  gap: 4px;
}

.critical-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover,
  &:focus-visible {
    background: #fafafa;

    .critical-item-arrow {
      opacity: 1;
      transform: translateX(2px);
    }
  }

  &.is-empty {
    opacity: 0.55;
  }
}

.critical-item-bar {
  width: 3px;
  align-self: stretch;
  border-radius: 0 3px 3px 0;
  background: var(--accent, #f5222d);
  flex-shrink: 0;
}

.critical-item-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}

.critical-item-label {
  flex: 1;
  font-size: 0.88rem;
  font-weight: 600;
  color: #262626;
  min-width: 0;
}

.critical-item-count {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}

.critical-item-ok {
  color: var(--dash-success);
  font-size: 16px;
  flex-shrink: 0;
}

.critical-item-arrow {
  color: #bfbfbf;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;
}

.critical-news {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 16px 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f0f7ff;
  border: 1px solid #d6e8ff;
}

.critical-news-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  color: #1890ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.critical-news-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 0.85rem;
    color: #262626;
  }

  span {
    font-size: 0.75rem;
    color: #8c8c8c;
  }
}

@media (max-width: 576px) {
  .critical-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .critical-news {
    flex-direction: column;
    align-items: flex-start;
  }
}

[data-kit-theme="dark"] {
  .critical-tasks-panel {
    border-color: #f5222d;
  }

  .critical-header {
    border-bottom-color: var(--dash-border, #232135);
  }

  .critical-header-icon {
    background: rgba(245, 34, 45, 0.15);
  }

  .critical-badge {
    background: rgba(245, 34, 45, 0.15);

    &--clear {
      background: rgba(82, 196, 26, 0.15);
    }
  }

  .critical-item:hover,
  .critical-item:focus-visible {
    background: #141322;
  }

  .critical-item-label {
    color: #aeaee0;
  }

  .critical-news {
    background: rgba(24, 144, 255, 0.1);
    border-color: #232135;
  }

  .critical-news-icon {
    background: #141322;
  }

  .critical-news-text strong {
    color: #aeaee0;
  }
}
</style>
